import React from 'react';
import styled from 'styled-components';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin: 2rem 0;
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  min-width: 40px;
  height: 40px;
  border: 1px solid #e2e8f0;
  background: ${props => props.active ? '#232d4d' : '#fff'};
  color: ${props => props.active ? '#fff' : '#334155'};
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? '#232d4d' : '#f8fafc'};
    border-color: #cbd5e1;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EllipsisSpan = styled.span`
  padding: 0 0.5rem;
  color: #64748b;
  font-weight: 500;
`;

const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  itemsPerPage = 6, 
  totalItems = 0 
}) => {
  if (totalPages <= 1) return null;

  const getVisiblePages = () => {
    const pages = [];
    const maxVisible = 7; // Maximum number of page buttons to show
    
    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 4) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 3) {
        pages.push('...');
      }
      
      // Always show last page
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const visiblePages = getVisiblePages();
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div>
      <PaginationContainer>
        {/* Previous button */}
        <PageButton
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          title="Previous page"
        >
          <FaChevronLeft size={12} />
        </PageButton>

        {/* Page numbers */}
        {visiblePages.map((page, index) => (
          page === '...' ? (
            <EllipsisSpan key={`ellipsis-${index}`}>...</EllipsisSpan>
          ) : (
            <PageButton
              key={page}
              active={page === currentPage}
              onClick={() => onPageChange(page)}
            >
              {page}
            </PageButton>
          )
        ))}

        {/* Next button */}
        <PageButton
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          title="Next page"
        >
          <FaChevronRight size={12} />
        </PageButton>
      </PaginationContainer>
      
      {/* Items info */}
      <div style={{ 
        textAlign: 'center', 
        color: '#64748b', 
        fontSize: '0.875rem',
        marginTop: '0.5rem'
      }}>
        Showing {startItem} to {endItem} of {totalItems} entries
      </div>
    </div>
  );
};

export default Pagination;
